import React, { memo } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    Stack,
    Box,
    Chip,
    List,
    ListItem,
    ListItemText,
    Divider,
    Paper,
    Grid,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Close as CloseIcon, Info as InfoIcon } from '@mui/icons-material';
import { ALERT_TYPES } from '../SourceSuspiciousTicksList';

// Group alert types by category for better organization
const ALERT_CATEGORIES = {
    'Market Manipulation': [
        'WASH_TRADE',
        'DETAILED_WASH_TRADE',
        'ARTIFICIAL_PRICE_MOVE',
        'PUMP_AND_DUMP',
        'BEAR_RAID'
    ],
    'Order Book Manipulation': [
        'SPOOFING_BID',
        'SPOOFING_ASK',
        'SPOOFING_BID_CANCEL',
        'SPOOFING_ASK_CANCEL',
        'LAYERING_BID',
        'LAYERING_ASK',
        'QUOTE_STUFFING'
    ],
    'Data Quality Issues': [
        'PRICE_OUT_OF_RANGE',
        'TRADE_OUTSIDE_SPREAD',
        'TIME_SEQUENCE_ERROR',
        'TIME_GAP',
        'INVALID_DATA'
    ],
    'Volume Anomalies': [
        'VOLUME_ANOMALY'
    ]
};

const getAlertTypeColor = (alertType) => {
    if (alertType?.includes('WASH_TRADE')) return 'error';
    if (alertType?.includes('SPOOFING')) return 'warning';
    if (alertType?.includes('LAYERING')) return 'info';
    if (alertType?.includes('PUMP') || alertType?.includes('DUMP') || alertType?.includes('RAID')) return 'error';
    if (alertType?.includes('TIME') || alertType?.includes('INVALID') || alertType?.includes('RANGE')) return 'secondary';
    return 'default';
};

const AlertTypesInfoModal = ({ open, onClose }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const getAlertTypeInfo = (alertType) => ALERT_TYPES.find(alert => alert.type === alertType);

    // Mobile 4-column layout helper
    const renderMobileLayout = () => {
        const allAlertTypes = Object.values(ALERT_CATEGORIES).flat();
        const pairs = [];
        
        for (let i = 0; i < allAlertTypes.length; i += 2) {
            pairs.push({
                first: allAlertTypes[i],
                second: allAlertTypes[i + 1]
            });
        }

        return (
            <Box sx={{ fontSize: '0.875rem' }}>
                {pairs.map((pair, index) => (
                    <Grid container spacing={1} key={index} sx={{ mb: 1 }}>
                        {/* First Key-Value Pair */}
                        <Grid item xs={3}>
                            <Typography variant="caption" fontWeight="bold" color="primary">
                                {pair.first?.replace(/_/g, ' ')}
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary">
                                {getAlertTypeInfo(pair.first)?.description || 'Market manipulation detection'}
                            </Typography>
                        </Grid>
                        
                        {/* Second Key-Value Pair */}
                        {pair.second && (
                            <>
                                <Grid item xs={3}>
                                    <Typography variant="caption" fontWeight="bold" color="primary">
                                        {pair.second?.replace(/_/g, ' ')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography variant="caption" color="text.secondary">
                                        {getAlertTypeInfo(pair.second)?.description || 'Market manipulation detection'}
                                    </Typography>
                                </Grid>
                            </>
                        )}
                          {/* Fill empty space if only one item */}
                        {!pair.second && (
                            <>
                                <Grid item xs={3} />
                                <Grid item xs={3} />
                            </>
                        )}
                    </Grid>
                ))}
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { minHeight: '70vh' }
            }}
        >
            <DialogTitle>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <InfoIcon color="primary" />
                        <Typography variant="h6">Alert Types Information</Typography>
                    </Stack>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        This system monitors real-time market data and identifies various types of suspicious trading activities. 
                        Below are the different alert types and their descriptions:
                    </Typography>
                </Box>

                {isMobile ? (
                    renderMobileLayout()
                ) : (
                    <Grid container spacing={3}>
                        {Object.entries(ALERT_CATEGORIES).map(([category, alertTypes]) => (
                            <Grid item xs={12} md={6} key={category}>
                                <Paper 
                                    variant="outlined" 
                                    sx={{ 
                                        p: 2, 
                                        height: '100%',
                                        bgcolor: 'background.neutral'
                                    }}
                                >
                                    <Typography 
                                        variant="subtitle1" 
                                        fontWeight="bold" 
                                        color="primary.main"
                                        gutterBottom
                                    >
                                        {category}
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    
                                    <List dense>
                                        {alertTypes.map((alertType) => {
                                            const alertInfo = getAlertTypeInfo(alertType);
                                            return (
                                                <ListItem 
                                                    key={alertType} 
                                                    alignItems="flex-start"
                                                    sx={{ px: 0, py: 1 }}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                                                <Chip
                                                                    label={alertType.replace(/_/g, ' ')}
                                                                    size="small"
                                                                    color={getAlertTypeColor(alertType)}
                                                                    variant="outlined"
                                                                />
                                                            </Stack>
                                                        }
                                                        secondary={
                                                            <Typography variant="body2" color="text.secondary">
                                                                {alertInfo?.description}
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Box sx={{ mt: 3, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="info.darker" gutterBottom>
                        <InfoIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        How It Works
                    </Typography>
                    <Typography variant="body2" color="info.dark">
                        Our system continuously analyzes market data in real-time using advanced algorithms to detect patterns 
                        that may indicate market manipulation or irregular trading behavior. Each alert is assigned a severity 
                        level (High, Medium, Low) based on the potential impact and confidence of the detection.
                    </Typography>
                </Box>

                <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.lighter', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="warning.darker" gutterBottom>
                        <InfoIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        Important Note
                    </Typography>
                    <Typography variant="body2" color="warning.dark">
                        These alerts are automated detections based on statistical analysis and pattern recognition. 
                        They should be used as indicators for further investigation and not as definitive proof of wrongdoing. 
                        Always conduct thorough analysis before taking any action.
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button variant="contained" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

AlertTypesInfoModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default memo(AlertTypesInfoModal);
